import {
  REFERRAL_COOKIE_CODE,
  REFERRAL_COOKIE_CODE_LAST,
  REFERRAL_COOKIE_VISITOR,
  REFERRAL_QUERY_PARAM,
  referralCodeSchema,
} from '@hero/schema';
import { CONFIG } from '@hero/core';

/**
 * Browser-side storage for the referral program — the TypeScript twin
 * of the vanilla `hero-r.js` embed (`apps/landing-v2/public/hero-r.js`).
 * Both write the same first-party cookies so a visit that starts on
 * `tryhero.io` and signs up on `app.tryhero.io` is one attribution:
 *
 * - `hero_r`      — the **original** referral code. First touch: never
 *                   overwritten while present, so the partner who
 *                   brought the visitor keeps the credit even if a
 *                   second link is clicked. This is what forms and
 *                   signup send as `referralCode`.
 * - `hero_r_last` — the **latest** code seen. Overwritten on every new
 *                   `?r=`; feeds PostHog's `referral_code_current` and
 *                   the hit's `context.originalCode` so a later campaign
 *                   can be analysed without changing who gets paid.
 * - `hero_vid`    — a random visitor id, used for server-side dedupe and
 *                   to stitch a cookie-only signup to its earlier hit.
 *
 * Cookies are scoped to the registrable domain when the host is a
 * `tryhero.io` subdomain, so landing and dashboard share them. Anywhere
 * else (localhost, preview URLs) they fall back to host-only.
 *
 * Every function is SSR-safe — returns `undefined` / no-ops when
 * `document` is missing.
 */

const DAY_S = 24 * 60 * 60;

const hasDocument = (): boolean => typeof document !== 'undefined';

const readCookie = (name: string): string | undefined => {
  if (!hasDocument()) return undefined;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
};

/** `.tryhero.io` for any `*.tryhero.io` host; `undefined` (host-only) elsewhere. */
const cookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  const match = host.match(/(?:^|\.)(tryhero\.io)$/);
  return match ? `.${match[1]}` : undefined;
};

const writeCookie = (name: string, value: string, maxAgeS: number): void => {
  if (!hasDocument()) return;
  const domain = cookieDomain();
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeS}`,
    'Path=/',
    'SameSite=Lax',
    ...(domain ? [`Domain=${domain}`] : []),
    ...(secure ? ['Secure'] : []),
  ].join('; ');
};

const randomId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/** Normalised code from a query string, or `undefined`. */
export const readReferralCodeFromSearch = (
  search: string | URLSearchParams,
): string | undefined => {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get(REFERRAL_QUERY_PARAM);
  if (!raw) return undefined;
  const parsed = referralCodeSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
};

const readCode = (cookie: string): string | undefined => {
  const raw = readCookie(cookie);
  if (!raw) return undefined;
  const parsed = referralCodeSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
};

/** The stored **original** (first-touch) code, when any. */
export const getReferralCode = (): string | undefined => readCode(REFERRAL_COOKIE_CODE);

/**
 * The **latest** code this browser saw. Falls back to the original when
 * only one code was ever captured.
 */
export const getReferralCodeCurrent = (): string | undefined =>
  readCode(REFERRAL_COOKIE_CODE_LAST) ?? getReferralCode();

/** Result of {@link storeReferralCode}. */
export type StoredReferral = {
  /** The original code now on file (unchanged when one already existed). */
  original: string;
  /** The code that was just captured — the new `current`. */
  current: string;
  /** `true` when this capture set the original. */
  firstTouch: boolean;
};

/**
 * Persist a code. The original (`hero_r`) is first-touch and never
 * overwritten; the latest (`hero_r_last`) always follows the capture.
 */
export const storeReferralCode = (code: string): StoredReferral => {
  const ttl = CONFIG.referral.cookieTtlDays * DAY_S;
  writeCookie(REFERRAL_COOKIE_CODE_LAST, code, ttl);
  const existing = getReferralCode();
  if (existing) return { original: existing, current: code, firstTouch: false };
  writeCookie(REFERRAL_COOKIE_CODE, code, ttl);
  return { original: code, current: code, firstTouch: true };
};

/** The visitor id, minted on first call. `undefined` only during SSR. */
export const getReferralVisitorId = (): string | undefined => {
  if (!hasDocument()) return undefined;
  const existing = readCookie(REFERRAL_COOKIE_VISITOR);
  if (existing && existing.length >= 8) return existing;
  const fresh = randomId();
  writeCookie(REFERRAL_COOKIE_VISITOR, fresh, CONFIG.referral.cookieTtlDays * DAY_S);
  return fresh;
};

/**
 * The two fields a form or signup payload carries so the backend can
 * attribute it: `referralCode` (when a code is stored) and
 * `referralVisitorId` (always, in the browser). Spread into
 * `sourceMeta` for `lead/public`, or top-level for `auth/sign-up`.
 */
export const getReferralAttributionFields = (): {
  referralCode?: string;
  referralOriginalCode?: string;
  referralVisitorId?: string;
} => {
  // The link the person converts through is the latest one they opened;
  // the first-touch code travels beside it as history.
  const original = getReferralCode();
  const referralCode = getReferralCodeCurrent() ?? original;
  const referralVisitorId = getReferralVisitorId();
  return {
    ...(referralCode ? { referralCode } : {}),
    ...(original && original !== referralCode ? { referralOriginalCode: original } : {}),
    ...(referralVisitorId ? { referralVisitorId } : {}),
  };
};

/**
 * Append the stored code to an outbound URL (dashboard hand-off,
 * `wa.me` deep link) so the next surface sees `?r=` even where cookies
 * do not travel. Leaves the URL untouched when nothing is stored.
 */
export const withReferralCode = (url: string, code = getReferralCode()): string => {
  if (!code) return url;
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    if (!parsed.searchParams.has(REFERRAL_QUERY_PARAM)) {
      parsed.searchParams.set(REFERRAL_QUERY_PARAM, code);
    }
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${REFERRAL_QUERY_PARAM}=${encodeURIComponent(code)}`;
  }
};
